<template>
  <PageContainer :breadcrumbs="breadcrumbs">
    <h1 class="pageHeader">{{ data?.seriesName }}収録パック</h1>
    <div class="Packs" v-if="data">
      <TextLink
        v-for="pack in data.packs"
        :key="pack.packId"
        :to="`/pokemon/series/${seriesId}/packs/${pack.packId}`"
        class="Pack"
        :text="getPackLinkLabel(pack)"
        size="lg"
      />
    </div>
  </PageContainer>
</template>

<script lang="ts">
import { computed } from "vue";
import { useSeoMeta, definePageMeta, createError, useHead, useRuntimeConfig } from "#imports";
import { defineNuxtComponent, useRouter, useAsyncData, useRequestHeaders } from "#app";
import type { PokemonPack } from "@/types/pokemon/pack";
import { timestampToDate } from "@/utils/proto/timestamp";
import { formatDisplayedDate } from "@/utils/date";
import { PokemonSeries } from "@/const/pokemon"
import PageContainer from '@/containers/Page'
import type { Breadcrumb } from '@/types/breadcrumb'
import type { ResponseType } from '@/server/api/pokemon/series/[seriesId]/packs/index.get'
import { TextLink } from '@ka-nabellinc/ka-nabell-ui'

export default defineNuxtComponent({
  components: {
    PageContainer,
    TextLink,
  },
  async setup() {
    const { public: { APP_ENV } } = useRuntimeConfig()

    const route = useRouter().currentRoute.value;
    const seriesId = computed<number | null>(() => 
      typeof route.params.seriesId === 'string' ? parseInt(route.params.seriesId, 10) : null
    )

    const getPackLinkLabel = (pack: PokemonPack) => {
      if (!pack.releasedAt) return "-";
      const date = timestampToDate(pack.releasedAt);
      const releaseDate = formatDisplayedDate(date);
      return `${pack.packName} (${releaseDate}発売)`;
    };

    definePageMeta({
      genre: "pokemon",
      footer: {
        isShowSocialMedia: true,
        isShowShopSeries: true,
      },
    });

    const { data, error } = await useAsyncData(`pokemonSeries_${seriesId.value}`, async () => {
      if (typeof seriesId.value !== 'number') {
        throw new Error(`series not found: ${seriesId.value}`)
      }
      const seriesName = PokemonSeries[seriesId.value]
      if (!seriesName) {
        throw new Error(`series not found: ${seriesId.value}`)
      }

      // 収録を取得
      const headers = useRequestHeaders()
      const { packs } = await $fetch<ResponseType>(`/api/pokemon/series/${seriesId.value}/packs`, {
        headers
      })

      return {
        seriesName,
        packs
      }
    })

    if (error.value || !data.value) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page not found',
        fatal: true
      })
    }

    const breadcrumbs: Breadcrumb[] = [
      {
        title: 'トレカ販売トップ',
        link: '/',
      },
      {
        title: 'ポケカ販売トップ',
        link: '/pokemon',
      },
      {
        title: data.value.seriesName
      },
    ]

    const title = data.value.seriesName + '収録パック - カーナベル'
    const description = `${data.value.seriesName}のカードを状態別に納得価格で販売中！最速で翌日午前に入手可能！`

    useSeoMeta({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      robots: APP_ENV==='production' ? 'index,follow' :'noindex,nofollow',
      twitterSite: '@ka_nabell_pcg',
      ogSiteName: 'ポケカ販売ならカーナベル！',
      ogUrl: `https://sell.ka-nabell.com/pokemon/series/${seriesId.value}`
    });

    useHead({
      link: [
        {
          rel: 'canonical',
          href: `https://sell.ka-nabell.com/pokemon/series/${seriesId.value}`
        }
      ]
    })

    return {
      seriesId,
      data,
      breadcrumbs,
      getPackLinkLabel,
    };
  },
});
</script>

<style scoped>
.Packs {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.pageHeader {
  width: 100%;
}
</style>
